import {computed, ref, watch} from 'vue'
import {useMeApiStore} from '@toolify/client/src/stores/api/MeApiStore/useMeApiStore'
import {IAvailableLlmTool} from '@toolify/server/src/controllers/UserController/validators/GetAvailableLlmTools/types/IGetAvailableLlmToolsRequestInterfaces'
import {isReadOnlyLlmTool} from '@toolify/client/src/modules/chat/functions/isReadOnlyLlmTool'
import {i18n} from '@toolify/client/src/setup/i18n'
import {resolveLocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/functions/resolveLocaleType'

const tools = ref<IAvailableLlmTool[]>([])
const isLoading = ref(false)
let loadPromise: Promise<void> | null = null
let loadedLocale: string | null = null

export function getLoadedLlmToolTitle(toolName: string): string | null {
  return tools.value.find(tool => tool.name === toolName)?.title ?? null
}

export function useAvailableLlmTools() {
  const meApiStore = useMeApiStore()

  async function loadTools(): Promise<void> {
    const locale = i18n.global.locale.value
    if (loadPromise && loadedLocale === locale) {
      return await loadPromise
    }
    loadedLocale = locale
    isLoading.value = true
    const currentLoadPromise: Promise<void> = meApiStore.getAvailableLlmTools(resolveLocaleType(locale))
      .then(response => {
        if(loadedLocale === locale) {
          tools.value = response.tools
        }
      })
      .catch(() => {
        if(loadPromise === currentLoadPromise) {
          loadPromise = null
        }
      })
      .finally(() => {
        isLoading.value = false
      })
    loadPromise = currentLoadPromise
    return await currentLoadPromise
  }

  function getAvailableTools(isReadOnly: boolean): IAvailableLlmTool[] {
    return isReadOnly ? tools.value.filter(isReadOnlyLlmTool) : tools.value
  }

  watch(i18n.global.locale, () => {
    if(loadedLocale) {
      void loadTools()
    }
  })

  const toolsByName = computed(() => new Map(tools.value.map(tool => [tool.name, tool])))

  return {
    tools,
    toolsByName,
    isLoading,
    loadTools,
    getAvailableTools,
  }
}
